import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { MagicAdLogo } from "@/components/Brand";
import { api, errorMessage, setSession } from "@/lib/api";
import { pendingCount } from "@/lib/pending";
import { useSeo } from "@/lib/seo";
import { ArrowRight, Loader2, Check, Images } from "lucide-react";

const PUNKTER = ["Optimaliserte bilder på 1–2 minutter", "Boligstylist som rydder, styler og møblerer – alltid merket", "Annonseutkast som ikke lover mer enn bildene viser"];

export default function AuthPage({ mode = "login" }) {
  useSeo({ title: mode === "login" ? "Logg inn" : "Opprett konto", description: mode === "login" ? "Logg inn på MagicAd by DigiHome." : "Opprett gratis konto og lag annonsebilder som leier ut.", noindex: true });
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/app";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const isRegister = mode === "register";
  const venter = pendingCount();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const r = isRegister
        ? await api.post("/auth/register", { email: email.trim(), password, name: name.trim() })
        : await api.post("/auth/login", { email: email.trim(), password });
      setSession(r.data.token, r.data.user);
      toast.success(isRegister ? `Velkommen, ${r.data.user.name || r.data.user.email}` : "Du er logget inn");
      nav(next.startsWith("/app") ? next : "/app", { replace: true });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const switchTo = `${isRegister ? "/logg-inn" : "/registrer"}${next !== "/app" ? `?next=${encodeURIComponent(next)}` : ""}`;

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.05fr_1fr] bg-background">
      {/* scene panel - DigiHome's own living room, the product as a thin layer */}
      <div className="relative hidden lg:flex flex-col justify-between overflow-hidden p-10 text-[hsl(var(--offwhite))]" data-testid="auth-scene">
        <img src="/v4/stue-2000.webp" alt="" className="absolute inset-0 h-full w-full object-cover" style={{ objectPosition: "50% 60%" }} draggable={false} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(21,18,15,0.35) 0%, rgba(21,18,15,0.15) 40%, rgba(21,18,15,0.82) 100%)" }} aria-hidden="true" />
        <Link to="/" className="relative inline-flex"><MagicAdLogo tone="dark" /></Link>
        <div className="relative max-w-[520px]">
          <h2 className="display text-[56px] xl:text-[68px]">Boligen din, på sin beste dag<span className="dot">.</span></h2>
          <ul className="mt-8 space-y-3">
            {PUNKTER.map((p) => <li key={p} className="flex items-start gap-3 text-[16px] text-[hsl(var(--offwhite))]/85"><span className="mt-[3px] h-5 w-5 shrink-0 rounded-full bg-[hsl(var(--accent))] text-[hsl(var(--ink))] flex items-center justify-center"><Check className="h-3 w-3" strokeWidth={2.2} /></span>{p}</li>)}
          </ul>
          <p className="mt-10 text-[12.5px] text-[hsl(var(--offwhite))]/50">© {new Date().getFullYear()} DigiHome · Originalene dine lagres uendret. Ingenting publiseres uten at du sier ja.</p>
        </div>
      </div>

      {/* form */}
      <div className="flex items-center justify-center px-5 py-10 sm:px-8">
        <div className="w-full max-w-[440px] space-y-8 rise-in" data-testid={`auth-${mode}`}>
          <Link to="/" className="lg:hidden inline-flex"><MagicAdLogo /></Link>
          <div>
            <div className="eyebrow">{isRegister ? "Opprett konto" : "Logg inn"}</div>
            <h1 className="display-sm mt-3 text-[40px] sm:text-[48px]">{isRegister ? "Kom i gang med MagicAd" : "Velkommen tilbake"}<span className="dot">.</span></h1>
            <p className="mt-3 text-[16px] text-dim">{isRegister ? "Gratis å opprette konto. Du betaler ingenting før du bruker bildene." : "Logg inn for å se annonsene dine."}</p>
          </div>
          {venter > 0 && (
            <div className="flex items-center gap-3 rounded-[12px] surface-accent border px-4 py-3 text-[14px]" data-testid="auth-pending-files">
              <Images className="h-4 w-4 shrink-0" /><span><span className="font-medium">{venter} bilde{venter === 1 ? "" : "r"} venter.</span> {isRegister ? "Opprett kontoen, så fortsetter vi med dem." : "Logg inn, så fortsetter vi med dem."}</span>
            </div>
          )}
          <form onSubmit={submit} className="panel p-6 sm:p-7 space-y-5">
            {isRegister && (
              <div className="space-y-2">
                <Label htmlFor="name" className="text-[13.5px] text-foreground/80">Navn</Label>
                <Input id="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Kari Nordmann" autoComplete="name" data-testid="auth-name" />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[13.5px] text-foreground/80">E-post</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="kari@eksempel.no" autoComplete="email" data-testid="auth-email" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-[13.5px] text-foreground/80">Passord</Label>
              <Input id="password" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} placeholder={isRegister ? "Minst 8 tegn" : "••••••••"} autoComplete={isRegister ? "new-password" : "current-password"} data-testid="auth-password" />
            </div>
            {error && <div className="rounded-[10px] surface-danger border px-3.5 py-2.5 text-[14px]" data-testid="auth-error">{error}</div>}
            <Button type="submit" size="lg" className="w-full" disabled={busy} data-testid="auth-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {busy ? "Vent litt…" : isRegister ? "Opprett konto" : "Logg inn"}
              {!busy && <ArrowRight className="h-4 w-4" />}
            </Button>
            <p className="text-[12.5px] text-svak leading-[1.45]">{isRegister ? "Ved å opprette konto bekrefter du at du har rett til bildene du laster opp." : "Kryptert forbindelse. Vi deler aldri bildene dine."}</p>
          </form>
          <p className="text-[14.5px] text-dim">
            {isRegister ? <>Har du konto? <Link to={switchTo} className="font-medium text-foreground underline-offset-4 hover:underline" data-testid="auth-switch">Logg inn</Link></>
              : <>Ny her? <Link to={switchTo} className="font-medium text-foreground underline-offset-4 hover:underline" data-testid="auth-switch">Opprett konto</Link></>}
            <span className="mx-2">·</span>
            <Link to="/lab" className="underline-offset-4 hover:underline" data-testid="auth-to-lab">DigiHome-ansatt?</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
